package one.healthium.data.common.serializer

import co.touchlab.kermit.Logger
import kotlinx.serialization.DeserializationStrategy
import kotlinx.serialization.KSerializer
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.buildClassSerialDescriptor
import kotlinx.serialization.encoding.CompositeDecoder
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.encoding.decodeStructure
import kotlinx.serialization.json.JsonContentPolymorphicSerializer
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import one.healthium.common.mapper.ColorMapper
import one.healthium.domain.common.Color

class ColorSerializers(
    private val colorThemeProvider: ColorThemeProvider,
    private val colorMapper: ColorMapper,
) : JsonContentPolymorphicSerializer<Color>(Color::class) {

    override fun selectDeserializer(element: JsonElement): DeserializationStrategy<Color> {
        return when (element) {
            is JsonObject -> ObjectColorSerializer(colorMapper)
            is JsonPrimitive -> StringColorSerializer(colorThemeProvider, colorMapper)
            else -> error("Unexpected JSON element")
        }
    }

    private class ObjectColorSerializer(
        private val colorMapper: ColorMapper,
    ) : KSerializer<Color> {
        override val descriptor: SerialDescriptor = buildClassSerialDescriptor("one.healthium.domain.common.Color") {
            element("light", PrimitiveSerialDescriptor("light", PrimitiveKind.STRING))
            element("dark", PrimitiveSerialDescriptor("dark", PrimitiveKind.STRING))
        }

        override fun deserialize(decoder: Decoder): Color {
            return decoder.decodeStructure(descriptor) {
                var light: String? = null
                var dark: String? = null
                while (true) {
                    when (val index = decodeElementIndex(descriptor)) {
                        0 -> light = decodeStringElement(descriptor, 0)
                        1 -> dark = decodeStringElement(descriptor, 1)
                        CompositeDecoder.DECODE_DONE -> break
                        else -> error("Unexpected index: $index")
                    }
                }
                Color(light?.let(colorMapper::parseColor), dark?.let(colorMapper::parseColor))
            }
        }

        override fun serialize(encoder: Encoder, value: Color) {
            TODO("Not used")
        }
    }

    private class StringColorSerializer(
        private val colorThemeProvider: ColorThemeProvider,
        private val colorMapper: ColorMapper,
    ) : KSerializer<Color> {
        override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("color", PrimitiveKind.STRING)

        override fun deserialize(decoder: Decoder): Color {
            val colorValue = decoder.decodeString()
            return map(colorValue) ?: Color(null, null)
        }

        override fun serialize(encoder: Encoder, value: Color) {
            TODO("Not used")
        }

        fun map(value: String?): Color? {
            return if (value != null) {
                val parsedColor = colorMapper.parseColor(value)
                val color = if (parsedColor != null) {
                    Color(parsedColor, parsedColor)
                } else {
                    colorThemeProvider.get(value)?.let { Color(it.first, it.second) }
                }
                if (color == null) {
                    Logger.e("can't parse color: $value")
                }
                color
            } else {
                null
            }
        }
    }
}